import { useState, useRef, useEffect, useCallback } from "react";
import { Send, ArrowLeft, Loader2, Smile } from "lucide-react";
import { useNavigate } from "react-router-dom";
import MessageBubble from "./MessageBubble";

const EMOJIS = [
  "😀","😂","😍","🥰","😎","🤔","😢","😡","👍","👎",
  "❤️","🔥","💯","🎉","😴","🤣","😅","🙏","💪","🌟",
  "😊","🥺","😭","🫡","💀","👀","🫂","✨","🙌","💬",
  "🎶","🍕","🎮","💎","🚀","🌈","🦋","💕","😈","🤝",
];

const formatDateLabel = (dateStr) => {
  const d = new Date(dateStr);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return "Today";
  if (d.toDateString() === yesterday.toDateString()) return "Yesterday";
  return d.toLocaleDateString([], {
    month: "short",
    day: "numeric",
    year: d.getFullYear() !== today.getFullYear() ? "numeric" : undefined,
  });
};

const ChatWindow = ({
  convo, messages, text, sending, scrollRef,
  setText, onSend, onBack, onMessageDeleted,
  isOnline, isTyping, sendTyping,
}) => {
  const navigate = useNavigate();
  const [showEmoji, setShowEmoji] = useState(false);
  const emojiRef = useRef(null);
  const typingDebounce = useRef(null);

  const name = convo?.other_user?.username || convo?.name || "Unknown";
  const avatar = convo?.other_user?.profile?.avatar;

  // Close emoji picker on outside click
  useEffect(() => {
    if (!showEmoji) return;
    const close = (e) => {
      if (emojiRef.current && !emojiRef.current.contains(e.target)) setShowEmoji(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [showEmoji]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      onSend();
    }
  };

  const handleInput = (e) => {
    setText(e.target.value);
    // Debounced typing event — max once per second
    clearTimeout(typingDebounce.current);
    typingDebounce.current = setTimeout(() => sendTyping?.(), 400);
  };

  const addEmoji = useCallback((emoji) => {
    setText((prev) => prev + emoji);
    setShowEmoji(false);
  }, [setText]);

  // Build message list with date separators
  const items = [];
  let lastDate = null;
  messages.forEach((msg) => {
    if (msg.created_at) {
      const msgDate = new Date(msg.created_at).toDateString();
      if (msgDate !== lastDate) {
        items.push({ type: "divider", date: msg.created_at, key: `d-${msgDate}` });
        lastDate = msgDate;
      }
    }
    items.push({ type: "msg", data: msg, key: msg.id });
  });

  if (!convo) {
    return (
      <div className="h-full flex flex-col items-center justify-center gap-3">
        <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center">
          <Send className="w-7 h-7 text-white/20" />
        </div>
        <p className="text-white/30 text-sm">Select a conversation</p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">

      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-white/10 bg-black/50 backdrop-blur-sm shrink-0">
        <button onClick={onBack} className="md:hidden p-2 rounded-full hover:bg-white/10 transition">
          <ArrowLeft className="w-5 h-5 text-white" />
        </button>

        <div
          onClick={() => navigate(`/profile/${name}`)}
          className="flex items-center gap-3 cursor-pointer group flex-1 min-w-0"
        >
          <div className="relative shrink-0">
            <div className="w-10 h-10 rounded-full overflow-hidden bg-white/10 flex items-center justify-center ring-2 ring-transparent group-hover:ring-white/20 transition-all">
              {avatar ? (
                <img src={avatar} alt={name} className="w-full h-full object-cover" />
              ) : (
                <span className="text-white font-bold">{name[0]?.toUpperCase()}</span>
              )}
            </div>
            {isOnline && (
              <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 rounded-full border-2 border-black" />
            )}
          </div>

          <div className="min-w-0">
            <p className="text-white font-semibold text-sm group-hover:text-white/80 transition truncate">
              @{name}
            </p>
            <p
              className="text-xs transition-all duration-300"
              style={{
                color: isTyping ? "#4ade80" : isOnline ? "rgba(74,222,128,0.55)" : "rgba(255,255,255,0.2)",
              }}
            >
              {isTyping ? "typing…" : isOnline ? "Active now" : "Offline"}
            </p>
          </div>
        </div>
      </div>

      {/* Messages */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto px-4 py-4 bg-gradient-to-b from-black via-black/95 to-black"
        style={{ scrollbarWidth: "none" }}
      >
        {messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full gap-3">
            <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center">
              {avatar ? (
                <img src={avatar} alt={name} className="w-full h-full rounded-full object-cover" />
              ) : (
                <span className="text-white/30 text-2xl font-bold">{name[0]?.toUpperCase()}</span>
              )}
            </div>
            <p className="text-white/20 text-sm">Say hi to @{name}! 👋</p>
          </div>
        ) : (
          <div className="space-y-4">
            {items.map((item) =>
              item.type === "divider" ? (
                <div key={item.key} className="flex items-center gap-3 py-1">
                  <div className="flex-1 h-px bg-white/[0.07]" />
                  <span className="text-white/25 text-[10px] font-medium shrink-0 select-none">
                    {formatDateLabel(item.date)}
                  </span>
                  <div className="flex-1 h-px bg-white/[0.07]" />
                </div>
              ) : (
                <MessageBubble key={item.key} message={item.data} onDeleted={onMessageDeleted} />
              )
            )}

            {/* Typing indicator */}
            {isTyping && (
              <div className="flex gap-2.5 justify-start">
                <div className="w-8 h-8 rounded-full overflow-hidden bg-white/10 flex items-center justify-center shrink-0 mt-1">
                  {avatar ? (
                    <img src={avatar} alt={name} className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-white font-bold text-xs">{name[0]?.toUpperCase()}</span>
                  )}
                </div>
                <div
                  className="px-4 py-3 rounded-2xl rounded-bl-md flex items-center gap-1.5"
                  style={{ background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.06)" }}
                >
                  {[0, 150, 300].map((delay) => (
                    <span
                      key={delay}
                      className="w-1.5 h-1.5 bg-white/45 rounded-full animate-bounce"
                      style={{ animationDelay: `${delay}ms` }}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Input */}
      <div className="px-4 pt-2 pb-3 border-t border-white/10 bg-black shrink-0">
        {/* Emoji picker */}
        {showEmoji && (
          <div
            ref={emojiRef}
            className="mb-2 p-3 rounded-2xl shadow-2xl grid gap-1"
            style={{
              background: "#161616",
              border: "1px solid rgba(255,255,255,0.09)",
              gridTemplateColumns: "repeat(8, minmax(0, 1fr))",
            }}
          >
            {EMOJIS.map((emoji) => (
              <button
                key={emoji}
                onClick={() => addEmoji(emoji)}
                className="text-xl hover:scale-125 active:scale-110 transition-transform duration-150 p-0.5 rounded"
              >
                {emoji}
              </button>
            ))}
          </div>
        )}

        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowEmoji((v) => !v)}
            className="p-2 rounded-full transition-all duration-200 shrink-0"
            style={{ color: showEmoji ? "#fff" : "rgba(255,255,255,0.3)" }}
          >
            <Smile className="w-5 h-5" />
          </button>

          <input
            value={text}
            onChange={handleInput}
            onKeyDown={handleKeyDown}
            placeholder="Message…"
            className="flex-1 bg-white/5 border border-white/10 rounded-full
              px-5 py-2.5 text-white text-sm outline-none
              placeholder-white/30 focus:border-white/20
              focus:bg-white/[0.08] transition-all duration-200"
          />

          <button
            onClick={() => onSend()}
            disabled={!text.trim() || sending}
            className={`p-2.5 rounded-full transition-all duration-300 shrink-0
              ${!text.trim() || sending
                ? "bg-white/5 text-white/20 cursor-not-allowed"
                : "bg-white text-black hover:bg-gray-100 active:scale-90"
              }`}
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChatWindow;
